// Upcoming Assignments tab — redesigned as a card grid.
//
// Each match is a rich card, matching the Umpiring Assignments look:
//   • Series ribbon + calendar-block date badge
//   • Big team headline with a gold "vs" pill
//   • Time + ground meta row
//   • Prominent poll-status band (green when open, muted when closed)
//   • Primary action button (Open Poll / Close Poll) + expand toggle
//   • Expanded body: umpire picker + colored nominee chips (assigned
//     get a green ribbon)
//
// When a poll opens the server queues an email notification to every
// committee-tier user.

import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarClock, MapPin, Clock, Trophy,
  ToggleLeft, ToggleRight, X, Plus, Gavel,
  ChevronDown, ChevronUp, CheckCircle2, Ban
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import { fmt12 } from "../shared";

// Give me Saturday + Sunday of the closest upcoming weekend (today
// counts if it's already Sat or Sun).
function upcomingWeekendIsos() {
  const now = new Date();
  const dow = now.getDay(); // 0 Sun … 6 Sat
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  let sat;
  if (dow === 6) sat = start;
  else if (dow === 0) sat = new Date(start.getTime() - 86400000);
  else { sat = new Date(start); sat.setDate(start.getDate() + (6 - dow)); }
  const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
  const toIso = d => d.toISOString().slice(0, 10);
  return { satIso: toIso(sat), sunIso: toIso(sun) };
}

function initials(u) {
  const f = (u.firstName || "").trim();
  const l = (u.lastName  || "").trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
}

const NOM_TONES = ["gold", "cyan", "green", "violet", "orange"];
function nomTone(u) {
  const seed = (u.firstName || "") + (u.lastName || "") + String(u.umpireId || 0);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return NOM_TONES[h % NOM_TONES.length];
}

// Split an ISO date "2026-07-11" into weekday/day/month parts so we
// can render the calendar-block badge without relying on toLocale
// running at the right time zone.
function splitDate(iso) {
  if (!iso) return { day: "—", dow: "", mon: "" };
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return { day: iso, dow: "", mon: "" };
  const dow = d.toLocaleDateString("en-US", { weekday: "short" });
  const mon = d.toLocaleDateString("en-US", { month: "short" });
  return { day: String(d.getDate()), dow, mon };
}

export default function UpcomingAssignmentsTab({ umpires, refresh: refreshOuter }) {
  const toast = useToast();
  const [matches,     setMatches]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [expandedId,  setExpandedId]  = useState(null);
  const [nomsById,    setNomsById]    = useState({});
  const [busyMap,     setBusyMap]     = useState({});
  const [addPickById, setAddPickById] = useState({});
  const [scope,       setScope]       = useState("weekend");

  const { satIso, sunIso } = useMemo(upcomingWeekendIsos, []);

  async function loadMatches() {
    setLoading(true);
    try {
      const rows = await api.listUpcomingMatches();
      setMatches(rows);
    } catch (err) {
      toast.error(err.message || "Couldn't load upcoming matches.");
    } finally { setLoading(false); }
  }
  useEffect(() => { loadMatches(); /* eslint-disable-next-line */ }, []);

  async function loadNominations(matchId) {
    try {
      const rows = await api.listMatchNominations(matchId);
      setNomsById(m => ({ ...m, [matchId]: rows }));
    } catch (err) {
      toast.error(err.message || "Couldn't load nominations.");
    }
  }

  function toggleExpanded(matchId) {
    if (expandedId === matchId) { setExpandedId(null); return; }
    setExpandedId(matchId);
    if (!nomsById[matchId]) loadNominations(matchId);
  }

  async function togglePoll(match) {
    const key = `poll-${match.id}`;
    setBusyMap(m => ({ ...m, [key]: true }));
    try {
      await api.toggleMatchPoll(match.id, !match.nominationsOpen);
      toast.success(match.nominationsOpen
        ? "Poll closed."
        : "Poll opened — committee members notified.");
      await loadMatches();
      if (nomsById[match.id]) await loadNominations(match.id);
    } catch (err) {
      toast.error(err.message || "Couldn't update poll.");
    } finally {
      setBusyMap(m => ({ ...m, [key]: false }));
    }
  }

  async function addNomination(matchId) {
    const umpireId = Number(addPickById[matchId]);
    if (!Number.isFinite(umpireId)) return;
    const key = `add-${matchId}`;
    setBusyMap(m => ({ ...m, [key]: true }));
    try {
      await api.addMatchNomination(matchId, umpireId);
      setAddPickById(m => ({ ...m, [matchId]: "" }));
      toast.success("Nomination added.");
      await Promise.all([loadNominations(matchId), loadMatches()]);
    } catch (err) {
      toast.error(err.message || "Couldn't add nomination.");
    } finally {
      setBusyMap(m => ({ ...m, [key]: false }));
    }
  }

  async function removeNomination(matchId, umpireId) {
    if (!confirm("Remove this nomination?")) return;
    try {
      await api.removeMatchNomination(matchId, umpireId);
      await Promise.all([loadNominations(matchId), loadMatches()]);
    } catch (err) {
      toast.error(err.message || "Couldn't remove nomination.");
    }
  }

  const visibleMatches = useMemo(() => {
    if (scope === "all14") return matches;
    return matches.filter(m => m.date === satIso || m.date === sunIso);
  }, [matches, scope, satIso, sunIso]);

  const boardStats = useMemo(() => {
    const total = visibleMatches.length;
    const open  = visibleMatches.filter(m => m.nominationsOpen).length;
    const readyish = visibleMatches.filter(m => m.assignedCount >= 2).length;
    return { total, open, readyish };
  }, [visibleMatches]);

  return (
    <Panel title={
      <div className="ump-poll-heading">
        <CalendarClock size={18}/>
        <div>
          <span>Upcoming Assignments</span>
          <small>
            {boardStats.total} match{boardStats.total === 1 ? "" : "es"} ·
            {" "}{boardStats.open} poll{boardStats.open === 1 ? "" : "s"} open ·
            {" "}{boardStats.readyish} locked in
          </small>
        </div>
        <div className="ump-scopeToggle" role="radiogroup" aria-label="Match window">
          <button
            type="button" role="radio" aria-checked={scope === "weekend"}
            className={`ump-scopeToggle-btn${scope === "weekend" ? " ump-scopeToggle-btn--active" : ""}`}
            onClick={() => setScope("weekend")}
          >This weekend</button>
          <button
            type="button" role="radio" aria-checked={scope === "all14"}
            className={`ump-scopeToggle-btn${scope === "all14" ? " ump-scopeToggle-btn--active" : ""}`}
            onClick={() => setScope("all14")}
          >Next 14 days</button>
        </div>
      </div>
    }>
      {loading && <p style={{color:"var(--muted)"}}>Loading matches…</p>}
      {!loading && visibleMatches.length === 0 && (
        <div className="league-annEmpty">
          <CalendarClock size={28}/>
          <p>
            {scope === "weekend"
              ? `No matches scheduled for ${satIso} — ${sunIso}. Switch to "Next 14 days" to see all upcoming matches.`
              : "No matches on the schedule in the next 14 days. Add matches under Schedule Management first."}
          </p>
        </div>
      )}

      <div className="ump-pollBoard">
        {visibleMatches.map(m => {
          const expanded  = expandedId === m.id;
          const noms      = nomsById[m.id] || [];
          const nominated = noms.filter(n => n.status === "Nominated");
          const assigned  = noms.filter(n => n.status === "Assigned");
          const alreadyIds = new Set(noms.map(n => n.umpireId));
          const options   = umpires.filter(u => !alreadyIds.has(u.id));
          const isOpen    = m.nominationsOpen;
          const isLocked  = m.assignedCount >= 2;
          const { day, dow, mon } = splitDate(m.date);

          const stateClass = isLocked
            ? "ump-pollMatch--locked"
            : isOpen
            ? "ump-pollMatch--open"
            : "ump-pollMatch--closed";

          return (
            <article key={m.id} className={`ump-pollMatch ${stateClass}`}>
              <header className="ump-pollMatch-head">
                {m.series ? (
                  <span className="ump-matchCard-series">
                    <Trophy size={11}/> {m.series}
                  </span>
                ) : <span/>}
                <div className="ump-matchCard-datebox">
                  <span className="ump-matchCard-dow">{dow}</span>
                  <span className="ump-matchCard-day">{day}</span>
                  <span className="ump-matchCard-mon">{mon}</span>
                </div>
              </header>

              <div className="ump-matchCard-teams">
                <span className="ump-matchCard-team">{m.teamOne}</span>
                <span className="ump-matchCard-vs">vs</span>
                <span className="ump-matchCard-team">{m.teamTwo}</span>
              </div>

              <div className="ump-matchCard-meta">
                {m.time && <span><Clock size={12}/> {fmt12(m.time)} ET</span>}
                <span><MapPin size={12}/> {m.ground}</span>
              </div>

              <div className={`ump-pollMatch-band ump-pollMatch-band--${isOpen ? "open" : isLocked ? "locked" : "closed"}`}>
                <span className="ump-pollMatch-bandStatus">
                  <Gavel size={12}/>
                  {isLocked ? "Locked in"
                    : isOpen ? "Poll open"
                    : "Poll closed"}
                </span>
                <span className="ump-pollMatch-bandCounts">
                  {m.nominatedCount} nominated · {m.assignedCount}/2 assigned
                </span>
              </div>

              <div className="ump-pollMatch-actions">
                <button
                  type="button"
                  className={`ump-pollMatch-primary ump-pollMatch-primary--${isOpen ? "close" : "open"}`}
                  onClick={() => togglePoll(m)}
                  disabled={busyMap[`poll-${m.id}`]}
                >
                  {isOpen
                    ? <><ToggleRight size={15}/> Close Poll</>
                    : <><ToggleLeft size={15}/> Open Poll</>}
                </button>
                <button
                  type="button"
                  className="ump-pollMatch-expand"
                  onClick={() => toggleExpanded(m.id)}
                  aria-expanded={expanded}
                  title={expanded ? "Hide nominees" : "Show nominees"}
                >
                  {expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  <span>{expanded ? "Hide nominees" : "Show nominees"}</span>
                </button>
              </div>

              {expanded && (
                <div className="ump-pollMatch-body">
                  {isOpen ? (
                    <form
                      className="ump-pollMatch-add"
                      onSubmit={e => { e.preventDefault(); addNomination(m.id); }}
                    >
                      <select
                        value={addPickById[m.id] || ""}
                        onChange={e => setAddPickById(v => ({ ...v, [m.id]: e.target.value }))}
                        disabled={options.length === 0}
                      >
                        <option value="">
                          {options.length === 0 ? "All umpires nominated" : "Nominate an umpire…"}
                        </option>
                        {options.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}{u.teamName ? ` · ${u.teamName}` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        type="submit"
                        className="ump-pollMatch-addBtn"
                        disabled={!addPickById[m.id] || busyMap[`add-${m.id}`]}
                      >
                        <Plus size={13}/> Nominate
                      </button>
                    </form>
                  ) : (
                    <p className="ump-pollMatch-hint">
                      Open the poll to accept umpire nominations for this match.
                    </p>
                  )}

                  {noms.length === 0 ? (
                    <p className="ump-pollMatch-hint">No nominations yet.</p>
                  ) : (
                    <>
                      {assigned.length > 0 && (
                        <div className="ump-pollMatch-group">
                          <h5><CheckCircle2 size={12}/> Assigned</h5>
                          <ul className="ump-pollMatch-chips">
                            {assigned.map(n => (
                              <li key={n.id} className="ump-pollMatch-chip ump-pollMatch-chip--assigned">
                                <div className="ump-pollMatch-chipAvatar">{initials(n)}</div>
                                <div>
                                  <b>{n.firstName} {n.lastName || ""}</b>
                                  {n.teamName && <small>{n.teamName}</small>}
                                </div>
                                <span className="ump-pollMatch-chipTag">Assigned</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {nominated.length > 0 && (
                        <div className="ump-pollMatch-group">
                          <h5><Gavel size={12}/> Nominated</h5>
                          <ul className="ump-pollMatch-chips">
                            {nominated.map(n => (
                              <li key={n.id} className={`ump-pollMatch-chip ump-pollMatch-chip--${nomTone(n)}`}>
                                <div className="ump-pollMatch-chipAvatar">{initials(n)}</div>
                                <div>
                                  <b>{n.firstName} {n.lastName || ""}</b>
                                  {n.teamName && <small>{n.teamName}</small>}
                                </div>
                                <button
                                  type="button"
                                  className="ump-pollMatch-chipRemove"
                                  onClick={() => removeNomination(m.id, n.umpireId)}
                                  aria-label="Remove nomination"
                                >
                                  <X size={12}/>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </Panel>
  );
}
